import { toSupportedJSONPickerType } from '@dittolive/anvil'
import * as Sentry from '@sentry/browser'
import dayjs from 'dayjs'

import { DATA_REQUEST_CUTOFF_DAYS, REFETCH_INTERVAL_MS } from './constants'
import {
  Device,
  DeviceLogResponse,
  DeviceRequestType,
  DeviceResponse,
  LastUpdatedOption,
} from './types'

export const lastUpdatedTimestamp = (option: LastUpdatedOption) => {
  if (option !== 'All time') {
    const timeMap: Record<string, [number, dayjs.ManipulateType]> = {
      '1 hour': [1, 'hour'],
      '12 hours': [12, 'hour'],
      '1 day': [1, 'day'],
      '1 week': [1, 'week'],
      '1 month': [1, 'month'],
    }

    const timeConfig = timeMap[option]

    if (timeConfig) {
      const [amount, unit] = timeConfig
      const timestamp = dayjs()
        .subtract(amount, unit)
        .utc()
        .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]') // Format to same format as stored in __small_peer_info

      return timestamp
    }
  } else {
    // "All time" can be set to a very old date. This is so it still hits the index.
    return '2018-01-01T00:00:00.000Z'
  }
}

export function reportError(err: unknown) {
  Sentry.withScope((scope) => {
    scope.setTag('useDeviceRPC', 'Error occurred during device RPC operation')
    Sentry.captureException(err)
  })
}

export function isDeviceLogResponse<T>(
  value: T | DeviceLogResponse,
): value is DeviceLogResponse {
  return (
    value !== null &&
    typeof value === 'object' &&
    'attachmentId' in value &&
    'timestamp' in value
  )
}

export function isDeviceResponse<T>(
  value: T | DeviceResponse,
): value is DeviceResponse {
  return (
    value !== null &&
    typeof value === 'object' &&
    'attachmentId' in value &&
    'timestamp' in value &&
    'type' in value &&
    typeof (value as DeviceResponse).type === 'string'
  )
}

/**
 * A function to determine the refetch interval based on the device's latest data request
 * pending status. If any data requests are pending, and haven't surpassed the cutoff days, then
 * the client will refetch each interval tick. Otherwise, the client will not refetch (i.e. return false)
 */
export const shouldRefetch = (device?: Device | null): number | false => {
  if (!device) {
    return false
  }

  const now = dayjs()

  // Helper to check if a request is complete (has response or error)
  const isRequestComplete = (
    timestamp: dayjs.Dayjs | undefined,
    attachmentId: string | undefined,
    type: DeviceRequestType,
  ): boolean => {
    if (!timestamp?.isValid()) return true

    // If we have an attachmentId, request is complete
    if (attachmentId) return true

    // Check if the latest response for this type has an error message
    const latestResponse = device.deviceResponses?.find(
      (r) => r.type === type && r.isLatest,
    )
    if (latestResponse?.errorMessage) return true

    return false
  }

  const requests = [
    {
      timestamp: device.latestLogsRequest,
      attachmentId: device.latestLogsAttachmentId,
      type: 'logs' as const,
    },
    {
      timestamp: device.latestBundleRequest,
      attachmentId: device.latestBundleAttachmentId,
      type: 'bundle' as const,
    },
    {
      timestamp: device.latestFlamegraphRequest,
      attachmentId: device.latestFlamegraphAttachmentId,
      type: 'flamegraph' as const,
    },
    {
      timestamp: device.latestMetricsRequest,
      attachmentId: device.latestMetricsAttachmentId,
      type: 'metrics' as const,
    },
  ]

  const hasPendingRequest = requests.some(
    ({ timestamp, attachmentId, type }) =>
      timestamp?.isValid() &&
      !isRequestComplete(timestamp, attachmentId, type) &&
      now.diff(timestamp, 'days') <= DATA_REQUEST_CUTOFF_DAYS,
  )

  return hasPendingRequest ? REFETCH_INTERVAL_MS : false
}

/**
 * A function to merge a list of metadata records into a single object.
 * This will try to avoid overwriting existing metadata keys with new values.
 */
export const resolveMetadataFromSampled = (
  metadataRecords: Record<string, unknown>[],
): Record<string, unknown> => {
  const mergedMetadata: Record<string, unknown> = {}

  metadataRecords.forEach((metadata) => {
    Object.entries(metadata).forEach(([key, value]) => {
      const _type = toSupportedJSONPickerType(value)
      if (_type && !mergedMetadata[key]) {
        mergedMetadata[key] = value
      }
    })
  })

  return mergedMetadata
}
